import type { Vendor } from "@/contexts/VendorContext";

const BASE = "/api/v1/vendors";

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...init,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error((err as { detail?: string }).detail ?? res.statusText);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

// API returns snake_case, frontend uses camelCase for contact fields
function mapResponse(data: Record<string, unknown>): Vendor {
  return {
    id: data.id as string,
    name: data.name as string,
    category: data.category as Vendor["category"],
    status: data.status as Vendor["status"],
    website: (data.website as string) ?? "",
    contactEmail: (data.contactEmail as string) ?? "",
    contactPhone: (data.contactPhone as string) ?? "",
    description: (data.description as string) ?? "",
    contractValue: (data.contractValue as string) ?? "",
    contractEnd: (data.contractEnd as string) ?? "",
  };
}

function mapRequest(data: Partial<Vendor>): Record<string, unknown> {
  const out: Record<string, unknown> = { ...data };
  if ("contactEmail" in data) {
    out["contact_email"] = data.contactEmail;
    delete out["contactEmail"];
  }
  if ("contactPhone" in data) {
    out["contact_phone"] = data.contactPhone;
    delete out["contactPhone"];
  }
  if ("contractValue" in data) {
    out["contract_value"] = data.contractValue;
    delete out["contractValue"];
  }
  if ("contractEnd" in data) {
    out["contract_end"] = data.contractEnd;
    delete out["contractEnd"];
  }
  return out;
}

export const vendorService = {
  list: (): Promise<Vendor[]> =>
    request<{ items: Record<string, unknown>[]; total: number; page: number; limit: number }>(
      BASE
    ).then((res) => res.items.map(mapResponse)),

  get: (id: string): Promise<Vendor> =>
    request<Record<string, unknown>>(`${BASE}/${id}`).then(mapResponse),

  create: (data: Omit<Vendor, "id">): Promise<Vendor> =>
    request<Record<string, unknown>>(BASE, {
      method: "POST",
      body: JSON.stringify(mapRequest(data)),
    }).then(mapResponse),

  update: (id: string, data: Partial<Vendor>): Promise<Vendor> =>
    request<Record<string, unknown>>(`${BASE}/${id}`, {
      method: "PUT",
      body: JSON.stringify(mapRequest(data)),
    }).then(mapResponse),

  delete: (id: string): Promise<void> =>
    request<void>(`${BASE}/${id}`, { method: "DELETE" }),
};
